import React, { useEffect, useRef, useCallback } from 'react';
import './TimelineHover.css';

function TimelineHover({ type, data, minute, position, below, onClose, onMouseEnter, onMouseLeave }) {
  const hoverRef = useRef(null);

  const handleClickOutside = useCallback((event) => {
    if (hoverRef.current && !hoverRef.current.contains(event.target)) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const getHoverTitle = useCallback(() => {
    if (type === 'objective') {
      return `Minute ${minute} - Objectives`;
    } else if (type === 'coaching') {
      return `Minute ${minute} - Coaching Points`;
    }
    return `Minute ${minute}`;
  }, [type, minute]);

  const renderObjective = useCallback((objective, index) => (
    <div key={index} className="hover-item">
      <div className="hover-item-header">
        <span className="hover-item-type">Objective</span>
        <span className="hover-item-task">{objective.task}</span>
      </div>
      <div className="hover-item-content">
        <div className="hover-item-objective">{objective.objective}</div>
        <div className="hover-item-context">{objective.context}</div>
      </div>
    </div>
  ), []);

  const renderCoachingPoint = useCallback((point, index) => (
    <div key={index} className="hover-item">
      <div className="hover-item-header">
        <span className="hover-item-type">Coaching Point #{point.increment}</span>
        <span className={`hover-item-priority ${point.priority}`}>
          {point.priority}
        </span>
      </div>
      <div className="hover-item-content">
        <div className="hover-item-suggestion">{point.suggestion}</div>
        <div className="hover-item-context">{point.context}</div>
        {point.reviewed && (
          <div className="hover-item-reviewed">
            <span className="hover-item-reviewed-badge">Reviewed</span>
            <div className="hover-item-reviewed-context">{point.reviewedContext}</div>
          </div>
        )}
      </div>
    </div>
  ), []);

  const renderItems = useCallback(() => {
    if (type === 'objective') {
      return data.map((objective, index) => renderObjective(objective, index));
    } else if (type === 'coaching') {
      return data.map((point, index) => renderCoachingPoint(point, index));
    }
    return null;
  }, [type, data, renderObjective, renderCoachingPoint]);

  const getHoverStyle = useCallback(() => {
    return {
      left: `${position.x}px`,
      top: `${position.y}px`,
      transform: below ? 'translateX(-50%) translateY(0%)' : 'translateX(-50%) translateY(-100%)'
    };
  }, [position, below]);

  const getHoverClass = useCallback(() => {
    return `timeline-hover ${below ? 'below' : ''}`.trim();
  }, [below]);

  return (
    <div 
      ref={hoverRef}
      className={getHoverClass()}
      style={getHoverStyle()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="timeline-hover-header">
        <h4>{getHoverTitle()}</h4>
        <button 
          className="timeline-hover-close"
          onClick={onClose}
          title="Close"
        >
          <i className="ti ti-x"></i>
        </button>
      </div>
      <div className="timeline-hover-content">
        {renderItems()}
      </div>
      <div className="timeline-hover-arrow"></div>
    </div>
  );
}

export default TimelineHover;
